// PiP 特性偵測（純函式，供單元測試）。APP#18 web A。

/**
 * 標準 + webkit fallback 的 video PiP 介面（型別擴充，不污染全域）。
 * 標準成員（requestPictureInPicture / disablePictureInPicture / videoWidth）由
 * lib.dom 的 HTMLVideoElement 提供；本介面只補 Safari 舊版的 webkit 等價路徑。
 * 標準成員以 Partial 放寬，方便單元測試構造最小假元素。
 */
export type PipVideoElement = Partial<HTMLVideoElement> & {
  // Safari 舊版（無標準 API 時）的等價路徑
  webkitSupportsPresentationMode?: (mode: string) => boolean;
  webkitSetPresentationMode?: (mode: 'picture-in-picture' | 'inline') => void;
  webkitPresentationMode?: 'inline' | 'picture-in-picture' | 'fullscreen';
};

/**
 * 此瀏覽器是否支援 video PiP：
 * - 標準：document.pictureInPictureEnabled 為 true；或
 * - webkit fallback：HTMLVideoElement.prototype 有 webkitSetPresentationMode。
 */
export function pipSupported(): boolean {
  if (typeof document !== 'undefined' && (document as Document & { pictureInPictureEnabled?: boolean }).pictureInPictureEnabled) {
    return true;
  }
  if (typeof HTMLVideoElement !== 'undefined') {
    const proto = HTMLVideoElement.prototype as unknown as PipVideoElement;
    if (typeof proto.webkitSetPresentationMode === 'function') return true;
  }
  return false;
}

/**
 * 此具體 <video> 當下能否進 PiP：
 * - 有畫面（videoWidth>0，避免對空元素呼叫拋錯）；
 * - 未被 disablePictureInPicture 關閉；
 * - 元素本身具備標準或 webkit 進 PiP 的方法。
 */
export function elementCanPip(v: PipVideoElement | null | undefined): boolean {
  if (!v) return false;
  if (v.disablePictureInPicture) return false;
  if (!v.videoWidth) return false;
  if (typeof v.requestPictureInPicture === 'function') return true;
  if (typeof v.webkitSetPresentationMode === 'function') {
    // webkitSupportsPresentationMode 若存在則用以二次確認（部分 audio-only 媒體不支援）
    return typeof v.webkitSupportsPresentationMode !== 'function' || v.webkitSupportsPresentationMode('picture-in-picture');
  }
  return false;
}
